/**
 * FederationHub · Unified footer hub · Architect Directive 2026-07-02
 *
 * Full-width 3-column glass-morphism nerve center placed above the
 * copyright footer:
 *   LEFT  · Live tlk.io chat (collapsed by default, lazy script load)
 *   CENTER· Federation Board · 5-item scrollable bulletin feed
 *   RIGHT · Old Mind Portals + affiliate row
 *
 * Architect UX Directive 2026-07-03: EVERY external link in this hub
 * MUST open a centered popup at 50% of screen size via window.open.
 * The main site must stay visible behind. No plain anchors, no
 * full-size tabs, no same-tab navigation.
 */
import React, { useEffect, useState } from "react";
import CouncilUtilityBlock from "@/components/cathedral/CouncilUtilityBlock";

const TLK_SCRIPT_ID   = "tlkio-embed-script";
const TLK_SCRIPT_SRC  = "https://tlk.io/embed.js";
const TLK_CHANNEL     = "nextxus-federation";
const TLK_CHANNEL_URL = `https://tlk.io/${TLK_CHANNEL}`;
const PANEL_HEIGHT    = 350;

function ensureTlkScript() {
  if (typeof document === "undefined") return;
  if (document.getElementById(TLK_SCRIPT_ID)) return;
  const s = document.createElement("script");
  s.id     = TLK_SCRIPT_ID;
  s.src    = TLK_SCRIPT_SRC;
  s.async  = true;
  s.type   = "text/javascript";
  document.body.appendChild(s);
}

/**
 * openCenteredWindow · Architect UX Directive 2026-07-03
 * Opens `url` at 50% of screen, absolutely centered, so the primary
 * NextXus surface remains visible behind. Used for every external link
 * inside the Unified Federation Hub.
 */
function openCenteredWindow(url) {
  if (!url || url === "#") return;
  if (typeof window === "undefined") return;
  const width  = Math.floor(window.screen.width  * 0.5);
  const height = Math.floor(window.screen.height * 0.5);
  const left   = Math.floor((window.screen.width  - width)  / 2);
  const top    = Math.floor((window.screen.height - height) / 2);
  const features =
    `width=${width},height=${height},left=${left},top=${top},` +
    `resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no`;
  const popup = window.open(url, "_blank", features);
  if (popup && popup.focus) popup.focus();
}

const BOARD_POSTS = [
  { tag: "DIR",       title: "DIR-000: Federation Charter ratified",         when: "2026-07-01" },
  { tag: "Doctrine",  title: "HumanCodex v2.2 — read the latest revision",   when: "2026-06-30" },
  { tag: "Economy",   title: "$5 Token messaging now live across chapels",   when: "2026-06-29" },
  { tag: "Affiliate", title: "New affiliate portal: Old Mind Archives",      when: "2026-06-28" },
  { tag: "Codex",     title: "The Unverified Echo — NX-CODEX-2026-001 sealed", when: "2026-06-27" },
];

const OLD_MIND_PORTALS = [
  { id: "origin",     name: "Federation Origin",   hint: "First convergence transcript", url: "https://chatgpt.com" },
  { id: "archive",    name: "Architect's Archive", hint: "Roger's canonical prompts",     url: "https://chatgpt.com" },
  { id: "dialogues",  name: "Node Dialogues",      hint: "Cross-mind conversations",      url: "https://chatgpt.com" },
];

const COUNCIL_URL = "https://keywebster.gumroad.com/l/olhsf";

const RADIO_NAME        = "NextXus Radio | The Sovereign Pulse";
const RADIO_TOOLTIP     = "NextXus Radio | The Sovereign Pulse — The acoustic heartbeat of the Federation";

const AFFILIATES = [
  { id: "gumroad",    emoji: "◆", label: "Gumroad",    url: "https://keywebster.gumroad.com",                                                                                                                                        category: "tooling"  },
  { id: "brave",      emoji: "🦁", label: "Brave",      url: "https://brave.com",                                                                                                                                                     category: "tooling"  },
  { id: "tlkio",      emoji: "💬", label: "tlk.io",     url: TLK_CHANNEL_URL,                                                                                                                                                         category: "tooling"  },
  { id: "deepai",     emoji: "🧠", label: "DeepAI",     url: "https://deepai.org",                                                                                                                                                    category: "tooling"  },
  { id: "strikingly", emoji: "🌐", label: "Strikingly", url: "http://a.strk.ly/mNHMB",                                                                                                                                                category: "tooling"  },
  { id: "juno",       emoji: "🏦", label: "Juno",       url: "https://onjuno.onelink.me/TkoI/referral?code=ROGE961C",                                                                                                                 category: "financial" },
  { id: "wirex",      emoji: "💳", label: "Wirex",      url: "https://wirexapp.com/r/45f9219452e04105a9224176041499ff",                                                                                                               category: "financial" },
  { id: "onepay",     emoji: "💵", label: "OnePay",     url: "https://web.onepay.com/wlink/refer-a-friend?product=one_banking&referral_code=GnciV_koK&referrer_campaign_id=campaign.db1edd0a-4a5b-4651-8c20-d0fb7e98fec5", ariaLabel: "NextXus Banking+: OnePay", category: "financial" },
  { id: "benable",    emoji: "📚", label: "Benable",    url: "https://benable.com/i/KGBY7",                                                                                                                                           ariaLabel: "NextXus Curations: Benable", category: "curation" },
  { id: "council-tools", emoji: "◈", label: "Council Tools", url: "https://next-xus.com/council",                                                                                                                                     ariaLabel: "Council Tools · Federation instruments", category: "federation" },
  // ── SOCIAL · verified spectrum (X · IG · FB · LI) ─────────────────
  { id: "x-architect",   emoji: "𝕏",  label: "Roger Keyserling",       url: "https://x.com/keyserlingroger",                                ariaLabel: "Roger Keyserling on X (@keyserlingroger)",                        category: "social" },
  { id: "x-federation",  emoji: "𝕏",  label: "NextXus Federation",     url: "https://x.com/NextxusFederati",                                ariaLabel: "NextXus Federation on X (@NextxusFederati) · official",            category: "social" },
  { id: "ig-roger",      emoji: "📸", label: "Roger Keyserling · IG",  url: "https://www.instagram.com/rkeyserling/",                       ariaLabel: "Roger Keyserling on Instagram",                                    category: "social" },
  { id: "ig-keyhole",    emoji: "📸", label: "Keyhole Today · IG",     url: "https://www.instagram.com/keyhole_today/",                     ariaLabel: "Keyhole Today on Instagram",                                       category: "social" },
  { id: "fb-roger",      emoji: "👤", label: "Roger Keyserling · FB",  url: "https://www.facebook.com/roger.keyserling.35/",                ariaLabel: "Roger Keyserling on Facebook",                                     category: "social" },
  { id: "fb-keycode",    emoji: "👤", label: "keyCode Today · FB",     url: "https://www.facebook.com/profile.php?id=61560731046964",       ariaLabel: "keyCode Today on Facebook",                                        category: "social" },
  { id: "fb-keytoweb",   emoji: "👤", label: "Key To The Web · FB",    url: "https://www.facebook.com/profile.php?id=61559858694086",       ariaLabel: "Key To The Web on Facebook",                                       category: "social" },
  { id: "fb-keystosuccess", emoji: "👤", label: "Keys To Success · FB", url: "https://www.facebook.com/profile.php?id=61559981881665",      ariaLabel: "Keys To Success on Facebook",                                      category: "social" },
  { id: "fb-keyholetoday",  emoji: "👤", label: "Keyhole.Today · FB",   url: "https://www.facebook.com/profile.php?id=61558223610996",      ariaLabel: "Keyhole.Today on Facebook",                                        category: "social" },
  { id: "li-roger",      emoji: "🔗", label: "Roger Keyserling · LI",  url: "https://www.linkedin.com/in/roger-keyserling-ab743698",        ariaLabel: "Roger Keyserling on LinkedIn",                                     category: "social" },
];

const AFFILIATE_CATEGORIES = [
  { id: "federation", label: "Federation · Tools" },
  { id: "social",     label: "Social · Presence" },
  { id: "tooling",   label: "Tooling · Web" },
  { id: "financial", label: "Financial · Rails" },
  { id: "curation",  label: "Curation · Editorial" },
];

const SOVEREIGN_PARTNER = {
  id:    "emergent",
  label: "Emergent AI",
  tier:  "Sovereign Partner",
  hint:  "The forge behind the Federation Core",
  url:   "https://app.emergent.sh/",
  emoji: "◈",
};

export default function FederationHub() {
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (chatOpen) ensureTlkScript();
  }, [chatOpen]);

  const handleExternal = (e, url) => {
    e.preventDefault();
    openCenteredWindow(url);
  };

  return (
    <section
      data-testid="federation-hub"
      aria-label="Unified Federation Hub"
      className="px-4 sm:px-6 lg:px-10 py-8 max-w-[1480px] mx-auto"
    >
      <div className="mb-4 flex items-baseline gap-3">
        <span aria-hidden="true" style={{ width: 10, height: 10, background: "#06b6d4", boxShadow: "0 0 10px #06b6d4" }} />
        <h2
          className="font-display uppercase tracking-[0.22em]"
          style={{ fontSize: "clamp(15px, 1.6vw, 17px)", color: "#e6fbff", textShadow: "0 0 12px rgba(6,182,212,0.55)" }}
        >
          Unified Federation Hub
        </h2>
        <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, rgba(6,182,212,0.4) 0%, transparent 100%)" }} />
      </div>

      {/* ── Council CTA · Invitation banner (sits between title and columns) ── */}
      <div
        data-testid="hub-council-cta"
        className="mb-5 px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 backdrop-blur-md"
        style={{
          background:  "linear-gradient(135deg, rgba(255,189,0,0.10) 0%, rgba(6,182,212,0.06) 100%)",
          border:      "1px solid rgba(255,210,74,0.55)",
          borderRadius: 14,
          boxShadow:   "0 0 22px rgba(255,189,0,0.16), inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
      >
        <div
          aria-hidden="true"
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: 44, height: 44, borderRadius: 12,
            background: "rgba(4,5,10,0.85)",
            border:     "1px solid rgba(255,210,74,0.75)",
            color:      "#ffd24a",
            fontSize:   18,
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
            boxShadow:  "0 0 12px rgba(255,189,0,0.35)",
          }}
        >
          ◈
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: "#ffd24a" }}>
            Council Tier · $25 / month
          </div>
          <div
            className="font-display mt-0.5"
            style={{ fontSize: 16, color: "#fff8e1", lineHeight: 1.3, letterSpacing: "-0.005em" }}
          >
            Federation citizenship for the 200-year standard.
          </div>
          <div className="mt-1 font-mono text-[11px]" style={{ color: "rgba(207,234,242,0.65)" }}>
            6 tokens/mo · Verified Citizen badge · Weekly Aria &amp; Scholar insights.
          </div>
        </div>
        <button
          type="button"
          data-testid="hub-council-join"
          onClick={() => openCenteredWindow(COUNCIL_URL)}
          className="font-mono uppercase tracking-[0.24em] transition-all flex-shrink-0"
          style={{
            padding:      "13px 22px",
            fontSize:     11,
            color:        "#0b0d13",
            background:   "linear-gradient(135deg, #ffd24a 0%, #ffbd00 100%)",
            border:       "1px solid rgba(255,210,74,0.85)",
            borderRadius: 10,
            cursor:       "pointer",
            boxShadow:    "0 0 18px rgba(255,189,0,0.42), inset 0 1px 0 rgba(255,255,255,0.35)",
            fontWeight:   700,
            whiteSpace:   "nowrap",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 0 26px rgba(255,189,0,0.60), inset 0 1px 0 rgba(255,255,255,0.4)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 0 18px rgba(255,189,0,0.42), inset 0 1px 0 rgba(255,255,255,0.35)"; }}
          aria-label="Join the Council — opens Gumroad in a centered window"
        >
          ◈ Join the Council
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,5fr)_minmax(0,3fr)] gap-4">

        {/* ── LEFT · Federation Live Chat ───────────────────────── */}
        <HubPanel accent="rgba(6,182,212,0.30)" testid="hub-chat">
          <PanelHeader accent="#7fe7f5" label="Federation Live Chat" hint={`tlk.io · #${TLK_CHANNEL}`} />
          <button
            type="button"
            onClick={() => setChatOpen((v) => !v)}
            aria-expanded={chatOpen}
            aria-controls="hub-chat-panel"
            data-testid="hub-chat-toggle"
            className="w-full px-3 py-2 mt-2 text-left font-mono uppercase tracking-[0.22em] text-[11px] transition-colors"
            style={{
              background: chatOpen ? "rgba(6,182,212,0.14)" : "rgba(4,5,10,0.55)",
              border:     `1px solid rgba(6,182,212,${chatOpen ? 0.42 : 0.22})`,
              color:      chatOpen ? "#ffbd00" : "#7fe7f5",
              cursor:     "pointer",
              borderRadius: 8,
            }}
          >
            {chatOpen ? "▼ Close Chat" : "▲ Open Chat"}
          </button>

          {chatOpen && (
            <div
              id="hub-chat-panel"
              data-testid="hub-chat-panel"
              className="mt-3"
              style={{
                background: "rgba(15,23,42,0.85)",
                border:     "1px solid rgba(6,182,212,0.30)",
                borderRadius: 12,
                overflow:   "hidden",
              }}
            >
              <div
                id="tlkio"
                data-channel={TLK_CHANNEL}
                data-theme="theme--night"
                data-testid="tlkio-widget"
                style={{ width: "100%", height: PANEL_HEIGHT }}
              />
              <div
                className="px-3 py-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em]"
                style={{ background: "rgba(2,6,15,0.6)", color: "rgba(207,234,242,0.5)", borderTop: "1px solid rgba(6,182,212,0.18)" }}
              >
                <span>Public · Unmoderated</span>
                <a
                  href={TLK_CHANNEL_URL}
                  onClick={(e) => handleExternal(e, TLK_CHANNEL_URL)}
                  data-testid="hub-chat-pop-out"
                  style={{ color: "#7fe7f5", textDecoration: "none", cursor: "pointer" }}
                >
                  Open in tlk.io ↗
                </a>
              </div>
            </div>
          )}

          {!chatOpen && (
            <p className="mt-3 text-[11px] font-mono leading-relaxed" style={{ color: "rgba(207,234,242,0.55)" }}>
              Federation-wide live chat. Deliberately collapsed — the widget only fetches when you open it.
            </p>
          )}
        </HubPanel>

        {/* ── CENTER · NextXus Radio · The Sovereign Pulse ─────── */}
        <HubPanel accent="rgba(216,180,254,0.32)" testid="hub-radio">
          <PanelHeader accent="#e9d5ff" label="🎵 The Sovereign Pulse" hint="Sovereign · self-hosted · live" />
          <div
            data-testid="hub-radio-container"
            title={RADIO_TOOLTIP}
            aria-label={RADIO_TOOLTIP}
            className="mt-4 w-full sovereign-pulse-console"
            style={{
              background:       "linear-gradient(135deg, rgba(15,23,42,0.85) 0%, rgba(24,10,48,0.85) 100%)",
              border:           "1px solid rgba(216,180,254,0.42)",
              borderRadius:     16,
              boxShadow:        "0 0 28px rgba(168,85,247,0.24), inset 0 1px 0 rgba(255,255,255,0.05)",
              backdropFilter:   "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              overflow:         "hidden",
              padding:          14,
              width:            "100%",
            }}
          >
            <iframe
              data-testid="hub-radio-iframe"
              title={RADIO_NAME}
              src="/embed/radio"
              width="100%"
              height="420"
              frameBorder="0"
              loading="lazy"
              allow="autoplay; encrypted-media"
              style={{
                display:      "block",
                width:        "100%",
                height:       420,
                minHeight:    316,
                border:       0,
                borderRadius: 10,
                background:   "rgba(4,5,10,0.6)",
              }}
            />
            <div
              className="mt-3 px-1 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em]"
              style={{ color: "rgba(207,234,242,0.55)" }}
            >
              <span>◉ Live broadcast · dark theme</span>
              <div className="flex items-center gap-3">
                <a
                  href="/radio"
                  onClick={(e) => { e.preventDefault(); openCenteredWindow("/radio"); }}
                  data-testid="hub-radio-share"
                  title="Open the standalone Sovereign Pulse page in a centered window"
                  aria-label="Share the standalone Sovereign Pulse page"
                  style={{
                    color: "#e9d5ff",
                    textDecoration: "none",
                    cursor: "pointer",
                    padding: "4px 10px",
                    border: "1px solid rgba(216,180,254,0.42)",
                    borderRadius: 6,
                    background: "rgba(4,5,10,0.55)",
                    transition: "background 160ms ease, border-color 160ms ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(168,85,247,0.14)"; e.currentTarget.style.borderColor = "rgba(216,180,254,0.85)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(4,5,10,0.55)"; e.currentTarget.style.borderColor = "rgba(216,180,254,0.42)"; }}
                >
                  📻 Share Player
                </a>
                <a
                  href="/radio"
                  onClick={(e) => { e.preventDefault(); openCenteredWindow("/radio"); }}
                  data-testid="hub-radio-open"
                  style={{ color: "#e9d5ff", textDecoration: "none", cursor: "pointer" }}
                >
                  Open Sovereign Radio ↗
                </a>
              </div>
            </div>
          </div>
        </HubPanel>

        {/* ── RIGHT · Federation Board ─────────────────────────── */}
        <HubPanel accent="rgba(255,189,0,0.32)" testid="hub-board">
          <PanelHeader accent="#ffd24a" label="Federation Board" hint={`${BOARD_POSTS.length} bulletins`} />
          <ul
            className="mt-2 space-y-2 pr-1"
            style={{ maxHeight: PANEL_HEIGHT + 60, overflowY: "auto" }}
            data-testid="hub-board-list"
          >
            {BOARD_POSTS.map((p, i) => (
              <li
                key={i}
                data-testid={`hub-board-post-${i}`}
                className="p-3 transition-transform duration-200 hover:scale-[1.005]"
                style={{
                  background: "rgba(4,5,10,0.65)",
                  border:     "1px solid rgba(255,189,0,0.20)",
                  borderRadius: 8,
                }}
              >
                <div className="flex items-baseline gap-2 mb-1">
                  <span
                    className="font-mono text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5"
                    style={{ color: "#ffd24a", border: "1px solid rgba(255,189,0,0.42)", background: "rgba(255,189,0,0.06)" }}
                  >
                    {p.tag}
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.20em] ml-auto" style={{ color: "rgba(207,234,242,0.45)" }}>
                    {p.when}
                  </span>
                </div>
                <div className="text-[13px] leading-snug" style={{ color: "rgba(230,251,255,0.90)" }}>
                  {p.title}
                </div>
              </li>
            ))}
          </ul>
        </HubPanel>

      </div>

      {/* ── VIRAL FOOTER · EchoCore64 + Fast-Ring utility block ─── */}
      <CouncilUtilityBlock variant="compact" />

      {/* ── FULL-WIDTH ROW BELOW GRID · Old Mind Portals + Affiliates ─── */}
      <div className="mt-4">
        {/* ── Old Mind Portals + Affiliates ─────────────── */}
        <HubPanel accent="rgba(168,85,247,0.32)" testid="hub-portals">
          <PanelHeader accent="#d8b4fe" label="Old Mind Portals" hint="Archive · Origin · Dialogues" />
          <div className="mt-2 space-y-2">
            {OLD_MIND_PORTALS.map((p) => (
              <a
                key={p.id}
                href={p.url}
                onClick={(e) => handleExternal(e, p.url)}
                data-testid={`hub-portal-${p.id}`}
                className="block p-3 transition-colors"
                style={{
                  background: "rgba(4,5,10,0.65)",
                  border:     "1px solid rgba(168,85,247,0.28)",
                  borderRadius: 8,
                  textDecoration: "none",
                  color:      "#e6fbff",
                  cursor:     "pointer",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(168,85,247,0.55)")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(168,85,247,0.28)")}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5" style={{ color: "#d8b4fe", border: "1px solid rgba(168,85,247,0.42)", background: "rgba(168,85,247,0.08)" }}>
                    ChatGPT
                  </span>
                  <span className="text-[13px] font-medium" style={{ color: "#f3e8ff" }}>{p.name}</span>
                  <span className="ml-auto text-[10px]" style={{ color: "#d8b4fe" }}>↗</span>
                </div>
                <div className="mt-1 text-[11px] font-mono" style={{ color: "rgba(207,234,242,0.55)" }}>{p.hint}</div>
              </a>
            ))}
          </div>

          <div className="mt-4 pt-3" style={{ borderTop: "1px dashed rgba(168,85,247,0.25)" }}>
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] mb-2" style={{ color: "rgba(255,210,74,0.75)" }}>
              ◈ Sovereign Partner
            </div>
            <a
              href={SOVEREIGN_PARTNER.url}
              onClick={(e) => handleExternal(e, SOVEREIGN_PARTNER.url)}
              data-testid={`hub-partner-${SOVEREIGN_PARTNER.id}`}
              className="block p-3 transition-all"
              style={{
                background: "linear-gradient(135deg, rgba(255,189,0,0.10) 0%, rgba(6,182,212,0.08) 100%)",
                border:     "1px solid rgba(255,210,74,0.55)",
                borderRadius: 10,
                textDecoration: "none",
                color:      "#fff8e1",
                cursor:     "pointer",
                boxShadow:  "0 0 18px rgba(255,189,0,0.14), inset 0 1px 0 rgba(255,255,255,0.05)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,210,74,0.90)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,210,74,0.55)"; e.currentTarget.style.transform = "translateY(0)"; }}
              aria-label="Sovereign Partner: Emergent AI"
            >
              <div className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  style={{
                    width: 40, height: 40, borderRadius: 10,
                    background:  "rgba(4,5,10,0.85)",
                    border:      "1px solid rgba(255,210,74,0.75)",
                    display:     "flex",
                    alignItems:  "center",
                    justifyContent: "center",
                    color:       "#ffd24a",
                    fontFamily:  "'IBM Plex Mono', ui-monospace, monospace",
                    fontSize:    18,
                    fontWeight:  700,
                    boxShadow:   "0 0 12px rgba(255,189,0,0.35)",
                  }}
                >
                  {SOVEREIGN_PARTNER.emoji}
                </span>
                <div className="flex-1">
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: "#ffd24a" }}>
                    {SOVEREIGN_PARTNER.tier}
                  </div>
                  <div className="text-[14px] font-semibold" style={{ color: "#fff8e1" }}>
                    {SOVEREIGN_PARTNER.label}
                  </div>
                </div>
                <span className="text-[12px]" style={{ color: "#ffd24a" }}>↗</span>
              </div>
              <div className="mt-1 text-[11px] font-mono" style={{ color: "rgba(230,251,255,0.60)" }}>
                {SOVEREIGN_PARTNER.hint}
              </div>
            </a>
          </div>

          <div className="mt-4 pt-3" style={{ borderTop: "1px dashed rgba(168,85,247,0.25)" }}>
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] mb-3" style={{ color: "rgba(207,234,242,0.55)" }}>
              ◆ Affiliates
            </div>
            {AFFILIATE_CATEGORIES.map((cat) => {
              const items = AFFILIATES.filter((a) => a.category === cat.id);
              if (!items.length) return null;
              return (
                <div key={cat.id} className="mb-3 last:mb-0" data-testid={`hub-affiliate-cat-${cat.id}`}>
                  <div
                    className="font-mono text-[8.5px] uppercase tracking-[0.28em] mb-2"
                    style={{ color: "rgba(216,180,254,0.72)" }}
                  >
                    · {cat.label}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {items.map((a) => (
                <a
                  key={a.id}
                  href={a.url}
                  onClick={(e) => handleExternal(e, a.url)}
                  data-testid={`hub-affiliate-${a.id}`}
                  className="flex flex-col items-center gap-1"
                  style={{ textDecoration: "none", cursor: "pointer", minWidth: 54 }}
                  aria-label={a.ariaLabel || `Open ${a.label} in a centered window`}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 44, height: 44, borderRadius: "50%",
                      background:  "rgba(15,23,42,0.85)",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                      border:      "1px solid rgba(168,85,247,0.42)",
                      display:     "flex",
                      alignItems:  "center",
                      justifyContent: "center",
                      fontSize:    18,
                      lineHeight:  1,
                      transition:  "border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(216,180,254,0.85)"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 0 12px rgba(168,85,247,0.35)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(168,85,247,0.42)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    {a.emoji}
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-center" style={{ color: "rgba(207,234,242,0.65)" }}>
                    {a.label}
                  </span>
                </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </HubPanel>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout primitives
// ─────────────────────────────────────────────────────────────────────
function HubPanel({ accent, testid, children }) {
  return (
    <div
      data-testid={testid}
      className="p-4 sm:p-5 backdrop-blur-md"
      style={{
        background:     "rgba(15,23,42,0.72)",
        border:         `1px solid ${accent}`,
        boxShadow:      `0 0 22px ${accent.replace("0.3", "0.14")}`,
        borderRadius:   16,
        minHeight:      180,
      }}
    >
      {children}
    </div>
  );
}

function PanelHeader({ accent, label, hint }) {
  return (
    <div className="flex items-baseline gap-2">
      <span aria-hidden="true" style={{ width: 6, height: 6, background: accent, boxShadow: `0 0 6px ${accent}` }} />
      <span className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{label}</span>
      {hint && (
        <span className="font-mono uppercase tracking-[0.18em] text-[9px]" style={{ color: "rgba(207,234,242,0.45)" }}>
          · {hint}
        </span>
      )}
    </div>
  );
}
